import { useEffect, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCookies } from 'react-cookie';
import { parse } from 'querystring';
import { Page } from '../../components/page';
import { PaymentBillingSection } from '../../components';
import { BillingSaveOutcome } from '../../components/payment-information-section/payment-billing/BillingDetailsForm';
import { LoadingProgress } from '../../layout';
import {
  fromAccounts,
  fromAuth,
  fromBillingInformation,
  fromCoupon,
  fromOrder,
  fromPaymentMethod,
  fromPromotion,
  fromSubscription,
  useAppDispatch,
} from '../../store';
import type { AppState } from '../../store';
import { QueryDictionary, useNavigate, useQuery } from '../../routes';
import {
  useGetQuotaAndCycleFromPathParams,
  useGetTrackingCookies,
  usePlanCartSnapshot,
} from '../../hook';
import { TermAndConditionCheck } from '../checkout/order-confirmation-section/TermAndConditionCheck';
import {
  paymentStatus as paymentStatusRoute,
  plan as planRoute,
} from '../../layout/routes';
import {
  Feature,
  getAddOnCodeByFeature,
  getIntervalPathParam,
  getProductId,
  getQuotaPathParam,
  getRecurlyAddOnName,
  getRecurlySubUuIdPathParam,
  getTrueBundleAddOnsPayload,
  getTrueBundleConfig,
  OrderPreviewRequest,
  OrderStatus,
  PaymentMethodType,
  PlanInterval,
  ProductPreviewRequest,
  SerializedException,
  SubscriptionSource,
} from '../../models';
import { useLaunchDarkly } from '../../launchdarkly';
import {
  CHECKOUT_ADDED_PRODUCTS_QUERY_PARAM,
  PARTNER_ID_COOKIE_NAME,
  PLAN_SUBSCRIPTION_ID_QUERY_PARAM,
  SUPPORTED_FEATURES,
  TRUE_TESTCLOUD_ADDON_FEATURES,
} from '../../utils/Constants';
import { getRecurlySubUuIdFromPathParam, sendTrackingData } from '../../utils/utils';
import { withCartSnapshot } from '../../utils/analytics';
import {
  getTargetSelfServeSubscription,
  parseSubscriptionId,
} from '../../utils/subscriptionTarget';
import {
  buildPreviewProducts,
  hasTrueBundleInPreviewRequests,
  isMonthlyToAnnualSwitchRequest,
  isMonthlyToAnnualSwitchPreview,
} from '../plan/component/true-platform/trueBundlePreview';
import {
  TRUE_MORE_TESTING_PRODUCT_FEATURES,
  TRUE_BUNDLE_PURCHASE_FEATURES,
  TRUE_BUNDLE_STARTER_FEATURES,
} from '../plan/component/true-platform/trueBundlePlans';
import { OrderSummary } from '../plan/component/true-platform/OrderSummary';
import { getManualPurchaseProductCode } from '../plan/component/true-platform/trueBundleProductCode';
import { PlanStepNav } from '../plan/component/PlanStepNav';
import { getStripe } from '../../utils/stripeClient';
import { AddProductsDialog, AddProductsResult } from './AddProductsDialog';
import { UnsavedChangesDialog, UnsavedChangesKind } from './UnsavedChangesDialog';
import { ADD_PRODUCTS_MAX_QUANTITY, buildAddProductsRows } from './addProductsRows';
import { describeCardFailure } from './cardFailureMessage';

/** PaymentIntent statuses after `handleNextAction` that mean the customer is done paying. */
const SETTLED_PAYMENT_INTENT_STATUSES = new Set(['succeeded', 'processing', 'requires_capture']);

// Preview failures CE reports when tax cannot be calculated for the billing address.
const TAX_PREVIEW_ERRORS = ['subscription.tax.service.exception', 'subscription.tax.service.unavailable'];

interface PendingCardAuthentication {
  orderId: number;
  clientSecret: string;
  /** The 3DS challenge was closed / failed; the page offers "Try again" or "Cancel order". */
  failed: boolean;
  message?: string;
}

const useStyles = makeStyles(theme => ({
  pageContent: {
    width: '100%',
    maxWidth: 1136,
    margin: '0 auto',
    padding: theme.spacing(2.5, 0, 5),
  },
  // Payment & Billing design (6.10): form 7/12, sticky Order Summary 5/12 (min 380px), 12px gap.
  // The summary NEVER drops below the form on a desktop window; only a phone-width viewport
  // (< sm), where two columns cannot physically fit, stacks.
  pageGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 7fr) minmax(380px, 5fr)',
    gap: theme.spacing(1.5),
    alignItems: 'start',
    [theme.breakpoints.down('dmd')]: {
      gridTemplateColumns: 'minmax(0, 7fr) minmax(340px, 5fr)',
    },
    [theme.breakpoints.down('tablet')]: {
      gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 320px)',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  formCard: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    padding: '20px 24px',
    backgroundColor: '#ffffff',
    border: '1px solid #eef2f0',
    borderRadius: 16,
    [theme.breakpoints.down('tablet')]: {
      padding: theme.spacing(2.5),
    },
  },
  pageTitle: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 20,
    fontWeight: 600,
    letterSpacing: '-.01em',
    lineHeight: 1.3,
    color: '#16141d',
  },
  continueButton: {
    width: '100%',
    height: 52,
    marginTop: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: '#0f8461',
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#0b6b4e',
      boxShadow: 'none',
    },
    '&:disabled': {
      backgroundColor: '#e1e7e4',
      color: '#7f8b86',
      opacity: 1,
    },
  },
  summaryTerms: {
    marginTop: theme.spacing(2),
    '& .MuiTypography-root': {
      fontSize: 12,
      lineHeight: 1.55,
    },
  },
}));

export const OrderConfirmationPage = () => {
  const { get, queryString, queryDictionary } = useQuery();
  const { replace, replaceQuery, redirect } = useNavigate();
  const { flags, initialized: ldInitialized, ecomEligibility } = useLaunchDarkly();
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const store = useStore<AppState>();
  const lastPreviewInputsRef = useRef<unknown[]>();
  const paymentInfoRef = useRef<any>(null);
  const checkoutDispatchedRef = useRef(false);
  // `pm_` tokenised from the inline card form for the checkout in flight (never stored in redux).
  const paymentMethodIdRef = useRef<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<'loading' | 'finished' | undefined>(undefined);
  // Inline card checkout: Stripe tokenisation error or BE decline shown under the card form.
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  // Saving a changed billing address can change the tax: the re-priced total is shown and the
  // customer confirms it with a second click instead of being charged a different amount.
  const [totalUpdatedNotice, setTotalUpdatedNotice] = useState<string | undefined>(undefined);
  // Pay now while a saved payment method / billing address is being edited (issue #1274 #1):
  // "Save your changes before paying?" — which section(s) are dirty, and whether "Save and pay"
  // is persisting them.
  const [unsavedEdits, setUnsavedEdits] = useState<UnsavedChangesKind | undefined>(undefined);
  const [unsavedSaving, setUnsavedSaving] = useState(false);
  // Inline card checkout: order PENDING on a 3DS challenge (plan D3: "Try again" / "Cancel order").
  const [pendingAuthentication, setPendingAuthentication] = useState<
  PendingCardAuthentication | undefined
  >(undefined);
  // "Add products" dialog of the Order Summary (design 6.10): the order is changed right here
  // instead of going back to the plan page.
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [cookies] = useCookies();

  const accountId = Number(get('accountId'));
  const featuresQuotaAndCycle = useGetQuotaAndCycleFromPathParams(SUPPORTED_FEATURES);

  // Store selectors
  const previewLoading = useSelector(fromOrder.selectPreviewLoading);
  const previewsTotal = useSelector(fromOrder.selectPreviewsTotal);
  const orderPreviewResponse = useSelector(fromOrder.selectOrderPreviewResponse);
  const orderResponse = useSelector(fromOrder.selectOrderResponse);
  const termsAndConditionsAccepted = useSelector(fromOrder.selectTermsAndConditionsAccepted);
  const couponName = useSelector(fromOrder.selectCouponName);
  const couponError = useSelector(fromOrder.selectCouponError);
  const couponInfo = useSelector(fromCoupon.selectCoupon);

  const selectedPaymentMethod = useSelector(fromPaymentMethod.selectSelectedPaymentMethod);
  const cardInformationInvalid = useSelector(fromPaymentMethod.isCardInformationInvalid);
  const user = useSelector(fromAuth.selectUser);
  const billingInfoLoading = useSelector(
    fromBillingInformation.selectLoadingByAccountId(accountId),
  );
  const orderLoading = useSelector(fromOrder.selectLoading);
  const currentBillingInformation = useSelector(
    fromBillingInformation.selectOneByAccountId(accountId),
  );

  const cartSnapshot = usePlanCartSnapshot();
  const trackingCookies = useGetTrackingCookies();
  const checkoutError = useSelector(fromOrder.selectCheckoutError);
  const currentSubscriptions = useSelector(fromSubscription.selectByAccountId(accountId));
  const recurlySubscriptions = useSelector(
    fromSubscription.selectSubscriptionsBySource(SubscriptionSource.RECURLY),
  );
  const hasPaidSubscription = useSelector(fromSubscription.hasPaidSubscription(accountId));
  // The campaigns price the Order Summary's package lines ("1 seat at $1,000/yr · Starter
  // package") and the dialog's Studio row; a direct landing here has not fetched them yet.
  const activePromotion = useSelector(fromPromotion.selectActivePromotion);
  const activeCampaigns = useSelector(fromPromotion.selectActiveCampaigns);

  const legacyTrueTeamQuota = featuresQuotaAndCycle[Feature.TRUE_TEAM]?.quota ?? 0;
  const targetSubscriptionId = parseSubscriptionId(get(PLAN_SUBSCRIPTION_ID_QUERY_PARAM));

  const buildAddOns = (): Record<string, number> => {
    if (legacyTrueTeamQuota === 0) {
      return {};
    }

    const addOns: Record<string, number> = {};
    TRUE_TESTCLOUD_ADDON_FEATURES.forEach(feature => {
      const quota = featuresQuotaAndCycle[feature]?.quota ?? 0;
      const addOnCode = getAddOnCodeByFeature(feature);
      if (quota > 0 && addOnCode) {
        addOns[getRecurlyAddOnName(addOnCode)] = quota;
      }
    });
    return addOns;
  };

  // Billing cycle
  const getRecurlySubscriptionUuid = (feature: Feature) => (
    getRecurlySubUuIdFromPathParam(get, feature)
    ?? getTargetSelfServeSubscription(
      recurlySubscriptions.filter(subscription => subscription.accountId === accountId),
      feature,
      targetSubscriptionId,
    )?.recurlySubUuId
  );

  // True Bundle Starters: rebuilt from the plan page's quota param exactly as the preview built
  // them - one package (quantity 1) plus the add-on set from `trueBundleConfig`, keyed by the BE
  // enum name. No subscription uuid: a bundle is never expanded or switched.
  const requestedBundleFeatures = TRUE_BUNDLE_STARTER_FEATURES
    .filter(feature => (featuresQuotaAndCycle[feature]?.quota ?? 0) > 0);
  const trueBundleStarterRequests: ProductPreviewRequest[] = requestedBundleFeatures
    .map(feature => ({
      productCode: getManualPurchaseProductCode(feature, PlanInterval.YEAR),
      quantity: 1,
      addOns: getTrueBundleAddOnsPayload(getTrueBundleConfig(flags?.trueBundleConfig, feature)),
    }))
    .filter(product => Object.keys(product.addOns ?? {}).length > 0);
  // The cart asks for a package LaunchDarkly no longer describes (the offer was pulled between
  // the plan page and here). Dropping it silently would let the customer pay for an order that
  // is missing what they picked, so the page says so and blocks Continue instead.
  const hasUnavailableBundle = requestedBundleFeatures.length > trueBundleStarterRequests.length;
  const trueBundleProductRequests = TRUE_BUNDLE_PURCHASE_FEATURES
    .map(feature => {
      const quantity = featuresQuotaAndCycle[feature]?.quota ?? 0;
      const cycleParam = featuresQuotaAndCycle[feature]?.cycle;
      const cycle = cycleParam ?? PlanInterval.YEAR;
      // Only associate the existing subscription when the user actually engaged
      // this SKU (added seats or explicitly picked a billing cycle). Otherwise an
      // untouched monthly TA/TP would be resurrected here as a phantom
      // monthly -> annual switch — both in the summary and the real checkout.
      const isEngaged = quantity > 0 || cycleParam !== undefined;
      const recurlySubscriptionUuid = isEngaged
        ? getRecurlySubscriptionUuid(feature)
        : undefined;
      return {
        productCode: getManualPurchaseProductCode(feature, PlanInterval.fromString(cycle)),
        quantity,
        ...(recurlySubscriptionUuid ? { recurlySubscriptionUuid } : {}),
      };
    })
    .filter(product => product.quantity > 0
      || isMonthlyToAnnualSwitchRequest(product, currentSubscriptions));
  const moreTestingProductRequests = TRUE_MORE_TESTING_PRODUCT_FEATURES
    .map(feature => {
      const quantity = featuresQuotaAndCycle[feature]?.quota ?? 0;
      const cycleParam = featuresQuotaAndCycle[feature]?.cycle;
      const cycle = cycleParam ?? PlanInterval.YEAR;
      // Mirror the True bundle logic: only associate the existing subscription
      // when the user engaged this SKU (added seats or picked a cycle), so an
      // untouched monthly product is not resurrected as a phantom switch.
      const isEngaged = quantity > 0 || cycleParam !== undefined;
      const recurlySubscriptionUuid = isEngaged
        ? getRecurlySubscriptionUuid(feature)
        : undefined;
      return {
        productCode: `${getProductId(feature)}_${
          PlanInterval.getPlanIntervalCode(PlanInterval.fromString(cycle))
        }`,
        quantity,
        ...(recurlySubscriptionUuid ? { recurlySubscriptionUuid } : {}),
      };
    })
    .filter(product => product.quantity > 0
      || isMonthlyToAnnualSwitchRequest(product, currentSubscriptions));
  const legacyTrueTeamCycle = featuresQuotaAndCycle[Feature.TRUE_TEAM]?.cycle ?? PlanInterval.YEAR;
  const legacyRecurlySubscriptionUuid = getRecurlySubscriptionUuid(Feature.TRUE_TEAM);
  const previewProductsRequest: ProductPreviewRequest[] = legacyTrueTeamQuota > 0
    ? [{
      productCode: `${getProductId(Feature.TRUE_TEAM)}_${
        PlanInterval.getPlanIntervalCode(PlanInterval.fromString(legacyTrueTeamCycle))
      }`,
      quantity: legacyTrueTeamQuota,
      ...(legacyRecurlySubscriptionUuid
        ? { recurlySubscriptionUuid: legacyRecurlySubscriptionUuid }
        : {}),
    }]
    : [
      ...trueBundleStarterRequests,
      ...trueBundleProductRequests,
      ...moreTestingProductRequests,
    ];
  const addOns = buildAddOns();
  const previewRequestProducts = previewProductsRequest.map((product, index) => (
    // A product that already carries its own add-ons is a True Bundle: its set is validated
    // against `trueBundleConfig` by the BE, so the legacy True Team add-on map must not be
    // merged into it.
    product.addOns
      ? product
      : {
        ...product,
        ...(index === 0 && Object.keys(addOns).length > 0 ? { addOns } : {}),
      }
  ));
  // Bank transfer is annual-only: any monthly item in the order hides the
  // option, mirroring the legacy checkout page.
  const hasMonthlyItem = previewRequestProducts.some(
    product => PlanInterval.getIntervalFromPlanCode(product.productCode) === PlanInterval.MONTH,
  );
  // The dialog re-prices the order by rewriting this page's quota / cycle params; the preview
  // effect below re-runs on this key (the subscription uuid is derived state, not an edit).
  const previewRequestKey = JSON.stringify(previewRequestProducts.map(
    ({ recurlySubscriptionUuid, ...product }) => product,
  ));
  const isLegacyTrueTeamOrder = legacyTrueTeamQuota > 0;
  const addProductsRows = buildAddProductsRows({
    featuresQuotaAndCycle,
    recurlySubscriptions: recurlySubscriptions.filter(
      subscription => subscription.accountId === accountId,
    ),
    targetSubscriptionId,
    isBundleInCart: hasTrueBundleInPreviewRequests(previewRequestProducts),
    planPrice: flags?.subscriptionPlanPrice,
    activePromotion,
    activeCampaigns,
    ecomEligibility: ecomEligibility ?? {},
    blockedHint: intl.formatMessage({ id: 'page.plan.v2.bundle.kse.not.combinable' }),
  });
  // Rows of the summary that can be changed in place (design 6.10): the self-serve products in
  // the order. Bundles are fixed packages, so they get neither a stepper nor a remove button.
  const editableFeatures = addProductsRows
    .filter(row => row.inOrderQuantity > 0)
    .map(row => row.feature);
  // Only a product added on THIS page ("+ Add more products") can be removed here; the products
  // chosen on the plan page are confirmed there and only change quantity (min 1). The set lives
  // in the URL like the rest of the order, so a refresh keeps the rule.
  const addedProductPrefixes = (get(CHECKOUT_ADDED_PRODUCTS_QUERY_PARAM) ?? '')
    .split(',')
    .filter(Boolean);
  const isProductRemovable = (feature: Feature) => addedProductPrefixes
    .includes(Feature.getFeaturePrefix(feature));
  // The dialog lists the add-ons added here (editable, 0 = remove) and what can still be added;
  // the plan-page products are not its business (design 6.10, "Add more products" flow).
  const addProductsDialogRows = addProductsRows.filter(
    row => row.inOrderQuantity === 0 || isProductRemovable(row.feature),
  );
  const canAddProducts = addProductsDialogRows.length > 0;
  const productsPreview = buildPreviewProducts(
    previewRequestProducts,
    orderPreviewResponse,
    flags?.subscriptionPlanPrice,
  );
  const selectedProducts = productsPreview.filter(product => product.newQuantity > 0
    || isMonthlyToAnnualSwitchPreview(product, currentSubscriptions));
  const hasSelectedProducts = selectedProducts.length > 0;
  const buildPreviewRequest = (): OrderPreviewRequest => ({
    singleProductList: previewRequestProducts,
  });

  // Inline card checkout (flag `inlineCardCheckoutEnabled`, same key as the BE): the card is
  // entered on this page and charged by the checkout request itself; the Checkout Session
  // redirect is the OFF path. While LaunchDarkly has not answered yet the page cannot know which
  // flow it is on, so checkout waits (a stale default would silently pick the session flow).
  const inlineCardEnabled = flags?.inlineCardCheckoutEnabled === true;
  const ldNotReady = ldInitialized === false;
  const isInlineCardCheckout = inlineCardEnabled
    && selectedPaymentMethod === PaymentMethodType.CREDIT_CARD;

  const buildCheckoutRequest = () => {
    const partnerId = cookies[PARTNER_ID_COOKIE_NAME];
    const paymentMethodId = isInlineCardCheckout ? paymentMethodIdRef.current : undefined;
    return {
      singleProductList: buildPreviewRequest().singleProductList,
      paymentMethod: selectedPaymentMethod,
      ...(couponName && { couponName }),
      ...(partnerId && { partnerId }),
      ...(paymentMethodId && { paymentMethodId }),
    };
  };

  // Billing information is validated (and saved) on the Continue click itself,
  // so an empty or out-of-sync form must not pre-disable the button here. With
  // Stripe Checkout Session the card is collected on Stripe's hosted page, so no
  // saved card is required to enable checkout; with the inline card form the
  // button waits until the entered card is complete (saved card = nothing to enter).
  // couponError blocks checkout: stale errors are cleared on mount, so a present
  // couponError means THIS page's preview dropped the applied coupon — proceeding
  // would silently charge full price against a discounted quote.
  // `loading` also covers the double-submit guard: it stays on from the click through
  // tokenisation, the checkout request and the 3DS challenge.
  const isCheckoutDisabled = loading || previewLoading
    || ldNotReady
    || !termsAndConditionsAccepted
    || !hasSelectedProducts
    || hasUnavailableBundle
    || !!couponError
    || !!pendingAuthentication
    || (isInlineCardCheckout && cardInformationInvalid)
    || (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER && hasMonthlyItem);

  const goToPaymentStatusPage = (orderId: number) => {
    replace(paymentStatusRoute.path, replaceQuery({
      ...queryDictionary(),
      accountId,
      orderId,
    }));
  };

  /**
   * Inline card checkout, 3DS branch: the BE left the order PENDING with the PaymentIntent's
   * client secret; Stripe.js runs the bank challenge and confirms the intent. Success → the
   * payment-status page polls until the webhook provisions the order. Closed / failed challenge
   * → the order stays PENDING (the intent is still `requires_action`), the customer can retry
   * the same secret or cancel the order and check out again. Declined after the challenge → the
   * order stays PENDING with an `open` invoice; the payment-status page offers the retry on the
   * Stripe hosted invoice page.
   */
  const runCardAuthentication = (orderId: number, clientSecret: string) => {
    setLoading(true);
    setPendingAuthentication({ orderId, clientSecret, failed: false });
    getStripe()
      .then(stripe => {
        if (!stripe) {
          throw new Error(intl.formatMessage({ id: 'checkout.card.not.ready' }));
        }
        return stripe.handleNextAction({ clientSecret });
      })
      .then(({ error, paymentIntent }) => {
        const settled = !error && paymentIntent
          && SETTLED_PAYMENT_INTENT_STATUSES.has(paymentIntent.status);
        if (settled) {
          setPendingAuthentication(undefined);
          goToPaymentStatusPage(orderId);
          return;
        }
        // The issuer declined (or authentication failed) AFTER the challenge: the PaymentIntent
        // is no longer retryable with this secret, but the order stays PENDING and its invoice
        // `open`. The payment-status page reads `paymentFailed` from the BE and offers "Retry
        // payment" on the Stripe hosted invoice page (same or another card).
        const declinedAfterAuthentication = error
          && (error.type === 'card_error'
            || error.payment_intent?.status === 'requires_payment_method');
        if (declinedAfterAuthentication) {
          setPendingAuthentication(undefined);
          goToPaymentStatusPage(orderId);
          return;
        }
        setPendingAuthentication({
          orderId,
          clientSecret,
          failed: true,
          message: error?.message,
        });
      })
      .catch((e: Error) => {
        setPendingAuthentication({
          orderId,
          clientSecret,
          failed: true,
          message: e?.message,
        });
      })
      .finally(() => setLoading(false));
  };

  const handleRetryCardAuthentication = () => {
    if (!pendingAuthentication) return;
    runCardAuthentication(pendingAuthentication.orderId, pendingAuthentication.clientSecret);
  };

  // "Cancel order" after an abandoned 3DS: voids the Stripe invoice, frees the account's
  // HAVE_PENDING_ORDER slot and re-enables checkout. The BE refuses to cancel an order whose
  // invoice got paid in the meantime (order.already.paid) — that order is on its way to PAID,
  // so the customer is sent to the status page instead of being asked to pay again.
  const handleCancelPendingOrder = () => {
    if (!pendingAuthentication) return;
    const { orderId } = pendingAuthentication;
    setLoading(true);
    dispatch(fromOrder.doCancelOrder(orderId))
      .then(result => {
        if (fromOrder.doCancelOrder.rejected.match(result)) {
          const message = (result.payload as { message?: string } | undefined)?.message;
          if (message === 'order.already.paid') {
            setPendingAuthentication(undefined);
            goToPaymentStatusPage(orderId);
            return;
          }
          setCardError(message || intl.formatMessage({ id: 'error.unknown' }));
          return;
        }
        setPendingAuthentication(undefined);
        setCheckoutStatus(undefined);
      })
      .catch(() => {
        setCardError(intl.formatMessage({ id: 'error.unknown' }));
      })
      .finally(() => setLoading(false));
  };

  // a message from the backend can be an i18n id (e.g. coupon.invalid.plan) or a
  // raw English sentence — render whichever form it is, fall back to error.unknown
  const renderServerMessage = (message?: string | null) => {
    if (message && intl.messages[message]) {
      return (
        <FormattedMessage
          id={message}
          values={{
            // same placeholder set the plan page's coupon renderer fills for
            // coupon.invalid.plan / coupon.min.license.required / coupon.min.amount.required
            quota: couponInfo?.rule?.minLicenses,
            amount: couponInfo?.rule?.minAmount,
            cycle: hasMonthlyItem ? 'annual' : 'monthly',
          }}
        />
      );
    }
    return message || intl.formatMessage({ id: 'error.unknown' });
  };

  // Clearing the coupon name re-runs the preview without it (see the preview effect below).
  const handleRemoveCoupon = () => {
    dispatch(fromOrder.clearCoupon());
  };

  const handleSelectPaymentMethod = (paymentMethod: PaymentMethodType) => {
    dispatch(fromPaymentMethod.doChangeSelectedPaymentMethod(paymentMethod));
    if (PaymentMethodType.BANK_TRANSFER === paymentMethod) {
      dispatch(fromPaymentMethod.doUpdateCardInformationInvalid(false));
    }
  };

  /**
   * The checkout itself. [presavedBilling] is the outcome of a billing save that "Save and pay"
   * already ran: the page then re-prices from it instead of saving the form again.
   */
  const runCheckout = (presavedBilling?: BillingSaveOutcome) => {
    const doCheckout = async () => {
      setLoading(true);
      setCardError(undefined);
      setTotalUpdatedNotice(undefined);
      // Validate the billing form and persist it when it changed; abort the
      // checkout when validation or saving fails.
      const totalBefore = previewsTotal;
      const billingOutcome = presavedBilling
        ?? await paymentInfoRef.current?.validateAndSaveBillingInformation?.();
      const billingReady = typeof billingOutcome === 'object' ? billingOutcome.ok : billingOutcome;
      const billingSaved = typeof billingOutcome === 'object' && billingOutcome.saved;
      // This closure's `currentBillingInformation` predates the save: use the address just written.
      const billingAddress = (typeof billingOutcome === 'object' && billingOutcome.billingInformation)
        || currentBillingInformation;
      if (billingReady === false) {
        setLoading(false);
        return;
      }
      // The address was (re)written: tax is priced from it, so re-run the preview before
      // charging. A different total is shown and confirmed with another click (never charge an
      // amount the customer has not seen).
      if (billingSaved && previewRequestProducts.length > 0) {
        const previewResult = await dispatch(fromOrder.doPreviewOrder({
          ...buildPreviewRequest(),
          ...(couponName && { couponName }),
        }));
        if (!fromOrder.doPreviewOrder.fulfilled.match(previewResult)) {
          // No trustworthy total to show → never charge; the summary shows the preview error.
          // Tax that cannot be resolved for the address says what to do about it (#1282); anything
          // else gets the generic retry text.
          const previewFailure = previewResult.payload as SerializedException | undefined;
          const previewErrorId = previewFailure?.message;
          setCardError(intl.formatMessage({
            id: previewErrorId && TAX_PREVIEW_ERRORS.includes(previewErrorId)
              ? previewErrorId
              : 'page.plan.v2.order.summary.preview.error.description',
          }));
          setLoading(false);
          return;
        }
        {
          const totalAfter = previewResult.payload.orderPreviewResponse.productsPreview
            .reduce((sum, product) => sum + product.total, 0);
          if (Math.abs(totalAfter - totalBefore) >= 0.005) {
            setTotalUpdatedNotice(intl.formatMessage(
              { id: 'page.order.confirmation.v2.total.updated' },
              { amount: intl.formatNumber(totalAfter, { style: 'currency', currency: 'USD' }) },
            ));
            setLoading(false);
            return;
          }
        }
      }
      // Inline card checkout with a new card: tokenise it with Stripe first (card data never
      // reaches CE). A card Stripe rejects (bad number, expired) stays on this page, no order.
      paymentMethodIdRef.current = undefined;
      if (isInlineCardCheckout && paymentInfoRef.current?.isEnteringNewCard?.()) {
        try {
          paymentMethodIdRef.current = await paymentInfoRef.current.createInlinePaymentMethod({
            email: user?.email,
            address: {
              country: billingAddress?.country,
              line1: billingAddress?.address1,
              city: billingAddress?.city,
              state: billingAddress?.state,
              postal_code: billingAddress?.postalCode,
            },
          });
        } catch (e: any) {
          setCardError(e?.message || intl.formatMessage({ id: 'checkout.card.invalid' }));
          setLoading(false);
          return;
        }
      }
      checkoutDispatchedRef.current = false;
      setCheckoutStatus('loading');
    };

    doCheckout().catch(() => {
      setLoading(false);
    });
  };

  // Pay now: a saved section still in edit mode with unsaved changes is asked about first
  // (design 6.10 "Save your changes before paying?"); edit modes with nothing changed are
  // simply closed and the order goes out with what is on file.
  const handleCheckout = () => {
    const edits = paymentInfoRef.current?.getUnsavedEdits?.();
    if (edits) {
      setUnsavedEdits(edits);
      return;
    }
    if (paymentInfoRef.current?.isEditing?.()) paymentInfoRef.current.discardEdits();
    runCheckout();
  };

  const handleDiscardAndPay = () => {
    paymentInfoRef.current?.discardEdits?.();
    setUnsavedEdits(undefined);
    runCheckout();
  };

  const handleSaveAndPay = async () => {
    setUnsavedSaving(true);
    // `loading` also holds the billing-change preview effect back: the checkout below re-prices
    // from the saved address itself, one preview instead of two.
    setLoading(true);
    try {
      const outcome = await paymentInfoRef.current?.saveEdits?.();
      setUnsavedEdits(undefined);
      // A failed save leaves its message in the section; the customer fixes it and pays again.
      if (outcome?.ok) {
        runCheckout(outcome.billing);
      } else {
        setLoading(false);
      }
    } finally {
      setUnsavedSaving(false);
    }
  };

  const handleBackToConfiguration = () => {
    // Back on the plan page every product is the customer's choice again.
    const { [CHECKOUT_ADDED_PRODUCTS_QUERY_PARAM]: added, ...dictionary } = parse(queryString());
    replace(planRoute.path, replaceQuery(dictionary as QueryDictionary));
  };

  // The dialog's result becomes this page's `<feature>Quota` / `<feature>Cycle` params - the same
  // params the plan page writes - so the preview request, the Order Summary and a refresh all
  // rebuild the same order. A removed product also drops its subscription-uuid param.
  const handleSaveProducts = (result: AddProductsResult, addedHere = false) => {
    const dictionary = { ...queryDictionary() } as Record<string, unknown>;
    const added = new Set(addedProductPrefixes);
    Object.entries(result).forEach(([featureKey, selection]) => {
      const feature = featureKey as Feature;
      if (!selection) return;
      if (selection.quantity > 0) {
        dictionary[getQuotaPathParam(feature)] = selection.quantity;
        dictionary[getIntervalPathParam(feature)] = selection.interval;
        if (addedHere) added.add(Feature.getFeaturePrefix(feature));
      } else {
        delete dictionary[getQuotaPathParam(feature)];
        delete dictionary[getIntervalPathParam(feature)];
        delete dictionary[getRecurlySubUuIdPathParam(feature)];
        added.delete(Feature.getFeaturePrefix(feature));
      }
    });
    if (added.size > 0) {
      dictionary[CHECKOUT_ADDED_PRODUCTS_QUERY_PARAM] = Array.from(added).join(',');
    } else {
      delete dictionary[CHECKOUT_ADDED_PRODUCTS_QUERY_PARAM];
    }
    setProductsDialogOpen(false);
    replace(undefined, replaceQuery(dictionary as QueryDictionary));
  };

  const handleChangeProductQuantity = (feature: Feature, quantity: number) => {
    const interval = addProductsRows.find(row => row.feature === feature)?.interval
      ?? PlanInterval.YEAR;
    // Same bounds as the dialog: a quantity typed into the row stepper can't exceed the cap.
    const bounded = Math.min(Math.max(1, quantity), ADD_PRODUCTS_MAX_QUANTITY);
    handleSaveProducts({ [feature]: { quantity: bounded, interval } });
  };

  const handleRemoveProduct = (feature: Feature) => {
    if (!isProductRemovable(feature)) return;
    handleSaveProducts({ [feature]: { quantity: 0, interval: PlanInterval.YEAR } });
  };

  // Same two fetches as the plan page on mount: the store already holds them when the customer
  // came from there, a direct landing (bookmark, refresh) needs them for the package lines.
  useEffect(() => {
    if (!activePromotion) {
      dispatch(fromPromotion.doFetchActivePromotion());
    }
  }, [activePromotion, dispatch]);
  useEffect(() => {
    if (activeCampaigns === undefined) {
      dispatch(fromPromotion.doFetchActiveCampaigns());
    }
  }, [activeCampaigns, dispatch]);

  // A couponError arriving here with NO coupon attached is stale: it belongs to the
  // plan page's coupon-less fallback flow (the user already saw and accepted the
  // coupon-less quote there) — it must not block this page's checkout.
  useEffect(() => {
    if (couponError && !couponName) {
      dispatch(fromOrder.clearCoupon());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect: redirect to paymentStatus after successful checkout
  useEffect(() => {
    const redirectToPaymentStatusPage = () => {
      if (!orderResponse) return;
      goToPaymentStatusPage(orderResponse.id);
    };

    // Inline card checkout answers in the checkout response itself (plan 4.1):
    // FULLY_RECEIVED = charged, PENDING + client secret = 3DS, PENDING + paymentFailed =
    // declined (the order and its `open` invoice are kept; the payment-status page offers the
    // retry on the Stripe hosted invoice page).
    const handleInlineCardCheckoutResult = () => {
      if (!orderResponse) return; // rejected checkout: the checkoutError alert below shows it
      if (orderResponse.status === OrderStatus.FAIL && !orderResponse.stripeInvoiceUrl) {
        // Rejected before any invoice existed (payment.method.invalid / .not.found): nothing to
        // retry on Stripe, so show it inline and let the next click create a new order.
        setCardError(describeCardFailure(intl, orderResponse.errorMessage));
        setCheckoutStatus(undefined);
        return;
      }
      if (orderResponse.status === OrderStatus.PENDING && orderResponse.stripeClientSecret) {
        runCardAuthentication(orderResponse.id, orderResponse.stripeClientSecret);
        return;
      }
      redirectToPaymentStatusPage();
    };

    const performCheckout = async () => {
      const result = await dispatch(fromOrder.doCheckout(buildCheckoutRequest()));
      // A created order can end the first-purchase window; the cached verdict would otherwise
      // keep showing the Starter layout on the next SPA visit to /plan (e.g. the bank-transfer
      // branch below navigates straight back there). The next visit re-asks the BE.
      if (fromOrder.doCheckout.fulfilled.match(result)) {
        dispatch(fromSubscription.invalidateNewAccount());
      }
      setCheckoutStatus('finished');
    };

    if (checkoutStatus !== 'loading') {
      checkoutDispatchedRef.current = false;
    }

    // one user intent = at most one checkout POST: the billing-information entity
    // gets a new reference on every upsert (e.g. a slow mount-time GET landing after
    // the click), which re-runs this effect while the status is still 'loading'
    if (checkoutStatus === 'loading' && currentBillingInformation && !checkoutDispatchedRef.current) {
      checkoutDispatchedRef.current = true;
      performCheckout().then(
        () => setLoading(false),
        error => {
          setLoading(false);
          throw error;
        },
      );
    }

    if (checkoutStatus === 'finished') {
      if (selectedPaymentMethod === PaymentMethodType.CREDIT_CARD && inlineCardEnabled) {
        handleInlineCardCheckoutResult();
      } else if (selectedPaymentMethod === PaymentMethodType.CREDIT_CARD) {
        const isPendingOrder = orderResponse?.status === OrderStatus.PENDING;
        const shouldRedirectToStripeCheckout = isPendingOrder
          && orderResponse?.stripeInvoiceUrl;

        if (shouldRedirectToStripeCheckout) {
          redirect(orderResponse!.stripeInvoiceUrl!);
        } else {
          redirectToPaymentStatusPage();
        }
      }

      if (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER) {
        // only a checkout that actually created an order may show the wire-transfer
        // instructions — a failed one falls through to the inline error alert
        if (!checkoutError && orderResponse) {
          replace(planRoute.path, replaceQuery({ accountId, showInstructionPopup: true }));
        }
      }
    }
  }, [checkoutStatus, currentBillingInformation]);

  const fetchInfo = async () => {
    if (!accountId) return;
    await Promise.all([
      dispatch(fromOrder.resetTermsAndConditionsAccepted()),
      dispatch(fromAccounts.doCheckPublicDomain(accountId)),
      dispatch(fromOrder.resetOrderPreviewRequest()),
      dispatch(fromSubscription.doFetchSubscriptions({ accountId })),
      dispatch(fromOrder.doFetchAllOrder(accountId)),
      dispatch(fromPaymentMethod.doGetDefaultPaymentMethod()),
      dispatch(fromAccounts.doCheckFirstThreeLicenses()),
    ]);
  };

  const sendCheckoutClickTracking = ({
    error,
    transactionId,
    invoiceNumbers,
  }: {
    error?: string;
    transactionId?: string | number;
    invoiceNumbers?: string[];
  } = {}) => {
    sendTrackingData(
      'checkout_page_all_action',
      accountId,
      undefined,
      withCartSnapshot({
        field: 'order_confirmation',
        object: 'button',
        action: 'checkout',
        plan_flow: legacyTrueTeamQuota > 0 ? 'true_team_legacy' : 'true_bundle',
        is_required: false,
        is_valid: !error,
        error,
        ...trackingCookies,
        ...(transactionId ? { transaction_id: transactionId } : {}),
        ...(invoiceNumbers && invoiceNumbers.length > 0 ? { invoice_numbers: invoiceNumbers } : {}),
      }, cartSnapshot),
    );
  };

  useEffect(() => {
    if (checkoutStatus !== 'finished') {
      // Action doCheckout - not dispatch yet
      return;
    }
    const hasCheckoutResult = !!checkoutError || !!orderResponse;
    if (!hasCheckoutResult) {
      return;
    }

    const isSuccessCheckout = !!orderResponse;
    if (isSuccessCheckout) {
      const invoiceNumbers = orderResponse?.singleProductInvoices;
      const transactionId = orderResponse?.id;

      sendCheckoutClickTracking({
        transactionId,
        invoiceNumbers,
      });
    } else {
      sendCheckoutClickTracking({
        error: checkoutError?.message,
      });
    }
  }, [checkoutError, orderResponse, checkoutStatus]);

  // Fetch order preview whenever quotas/cycle change (the Add products / Edit order dialog
  // rewrites the params); keep a coupon applied on the plan page attached to this preview as
  // well. The Order Summary's coupon field applies / removes a coupon by changing its name,
  // which re-prices here.
  useEffect(() => {
    if (!accountId || loading || billingInfoLoading || orderLoading) return;
    // The section mounts once the page data has loaded and may start its billing GET just before
    // this run, whose render still saw "not loading": read the live state, or the order is priced
    // once without the address and again when the GET settles.
    if (fromBillingInformation.selectLoadingByAccountId(accountId)(store.getState())) return;
    // An empty product list is a real state now that R6 (Decision 16) stopped reading the retired
    // True Automation / True Platform params: an old bookmark carrying only `trueAutomationQuota`
    // lands here with nothing to price. The BE preview does `.first()` on the list and would throw,
    // turning a stale link into an error screen instead of an empty summary.
    if (previewRequestProducts.length === 0) return;
    // A run caused only by the billing lookup settling with nothing to change (no address on file,
    // or the lookup failed) would re-send the very same preview.
    const inputs = [currentBillingInformation, couponName, previewRequestKey];
    if (lastPreviewInputsRef.current?.every((input, index) => input === inputs[index])) return;
    lastPreviewInputsRef.current = inputs;
    dispatch(fromOrder.doPreviewOrder({
      ...buildPreviewRequest(),
      ...(couponName && { couponName }),
    }));
    // `billingInfoLoading` is a dependency on purpose: the section's mount-time billing GET is
    // already pending when this first runs (child effects run first), and an account with NO
    // address on file never changes `currentBillingInformation` — without it the order of a
    // first-time buyer was never priced ("Your cart is empty").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBillingInformation, billingInfoLoading, couponName, previewRequestKey]);

  const isBankTransfer = selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER;
  // Design 6.10: "Pay now" for a card (charged on this page with the inline form) and "Place
  // order" for bank transfer; the Checkout Session redirect keeps its "Continue to payment".
  let continueLabelId = 'page.order.confirmation.continue.payment';
  if (isBankTransfer) {
    continueLabelId = 'page.order.confirmation.v2.place.order';
  } else if (inlineCardEnabled) {
    continueLabelId = 'page.order.confirmation.continue.pay';
  }

  const checkoutActions = (
    <>
      <Box className={classes.summaryTerms}>
        <TermAndConditionCheck redesign />
      </Box>
      <Button
        id="order-confirmation-continue"
        variant="contained"
        className={classes.continueButton}
        onClick={handleCheckout}
        disabled={isCheckoutDisabled}
      >
        <FormattedMessage id={continueLabelId} />
      </Button>
    </>
  );

  return (
    <Page
      id="order-confirmation"
      headerId="page.order.confirmation.header"
      fetchData={fetchInfo}
      background="#fafafd"
      hideHeader
    >
      <Box sx={{ width: '100%', maxWidth: 1136, mx: 'auto' }}>
        <PlanStepNav currentStep={2} onBackToPlan={handleBackToConfiguration} />
      </Box>
      <Box className={classes.pageContent}>
        <Box className={classes.pageGrid}>
          <Box component="section" className={classes.formCard}>
            <Typography component="h1" className={classes.pageTitle}>
              <FormattedMessage id="page.order.confirmation.v2.title" />
            </Typography>

            <PaymentBillingSection
              ref={paymentInfoRef}
              accountId={accountId}
              availableRenewal
              hasMonthlyItem={hasMonthlyItem}
              hasPaidSubscription={hasPaidSubscription}
              handleSelectPaymentMethod={handleSelectPaymentMethod}
              inlineCardEnabled={inlineCardEnabled}
            />

            {cardError && (
              <Alert id="order-confirmation-card-error" severity="error" sx={{ mt: 2 }}>
                {cardError}
              </Alert>
            )}
            {totalUpdatedNotice && (
              <Alert id="order-confirmation-total-updated" severity="info" sx={{ mt: 2 }}>
                {totalUpdatedNotice}
              </Alert>
            )}
            {pendingAuthentication?.failed && (
              <Alert
                id="order-confirmation-authentication-failed"
                severity="warning"
                sx={{ mt: 2 }}
                action={(
                  <>
                    <Button
                      id="order-confirmation-authentication-retry"
                      color="inherit"
                      size="small"
                      disabled={loading}
                      onClick={handleRetryCardAuthentication}
                    >
                      <FormattedMessage id="checkout.card.authentication.retry" />
                    </Button>
                    <Button
                      id="order-confirmation-cancel-order"
                      color="inherit"
                      size="small"
                      disabled={loading}
                      onClick={handleCancelPendingOrder}
                    >
                      <FormattedMessage id="checkout.card.authentication.cancel" />
                    </Button>
                  </>
                )}
              >
                <FormattedMessage id="checkout.card.authentication.failed" />
                {pendingAuthentication.message ? ` ${pendingAuthentication.message}` : ''}
              </Alert>
            )}

            {hasUnavailableBundle && (
              <Alert id="order-confirmation-bundle-unavailable" severity="error" sx={{ mt: 3 }}>
                <FormattedMessage id="bundle.sale.ended" />
              </Alert>
            )}
            {couponError && (
              <Alert
                severity="error"
                sx={{ mt: 3 }}
                action={(
                  <Button color="inherit" size="small" onClick={handleRemoveCoupon}>
                    <FormattedMessage id="page.order.confirmation.coupon.remove" />
                  </Button>
                )}
              >
                {renderServerMessage(couponError)}
              </Alert>
            )}
            {checkoutStatus === 'finished' && checkoutError && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {renderServerMessage(checkoutError.message)}
              </Alert>
            )}
          </Box>

          <Box component="aside" id="order-confirmation-summary">
            <OrderSummary
              isAnnual={!hasMonthlyItem}
              groupByInterval
              showCoupon
              previewRequests={previewRequestProducts}
              variant="checkout"
              onAddProducts={isLegacyTrueTeamOrder
                ? handleBackToConfiguration
                : () => setProductsDialogOpen(true)}
              canAddProducts={isLegacyTrueTeamOrder || canAddProducts}
              editableFeatures={isLegacyTrueTeamOrder ? [] : editableFeatures}
              isProductRemovable={isProductRemovable}
              onChangeProductQuantity={handleChangeProductQuantity}
              onRemoveProduct={handleRemoveProduct}
              checkoutActions={checkoutActions}
            />
          </Box>
        </Box>
      </Box>
      {!isLegacyTrueTeamOrder && (
        <AddProductsDialog
          open={productsDialogOpen}
          rows={addProductsDialogRows}
          onClose={() => setProductsDialogOpen(false)}
          onSave={result => handleSaveProducts(result, true)}
        />
      )}
      <UnsavedChangesDialog
        open={!!unsavedEdits}
        what={unsavedEdits ?? 'billing'}
        busy={unsavedSaving}
        onClose={() => setUnsavedEdits(undefined)}
        onDiscardAndPay={handleDiscardAndPay}
        onSaveAndPay={handleSaveAndPay}
      />
      {(loading || previewLoading) && <LoadingProgress fullscreen />}
    </Page>
  );
};
